import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { MapDefinition } from '@grs-atlas/shared';
import { isAdmin } from '@grs-atlas/shared';
import { useAuthRoles } from '../../auth/AuthContext';
import { Toast } from '../../components/Toast';
import { useMap, useUpdateMap } from '../../services/hooks';
import { MapFieldsEditor } from './MapFieldsEditor';

export function AdminMapPage() {
  const { mapId = '' } = useParams();
  const roles = useAuthRoles();
  const mapQuery = useMap(mapId);
  const updateMap = useUpdateMap();
  const [draft, setDraft] = useState<MapDefinition | null>(null);
  const [lockedKeys, setLockedKeys] = useState<ReadonlySet<string>>(new Set());
  const [toast, setToast] = useState<{ message: string; type?: 'error' | 'success' } | null>(
    null,
  );

  useEffect(() => {
    if (mapQuery.data) {
      setDraft(structuredClone(mapQuery.data));
      setLockedKeys(new Set(mapQuery.data.fields.map((f) => f.key).filter(Boolean)));
    }
  }, [mapQuery.data]);

  if (!isAdmin(roles)) {
    return <div className="empty">Administrator role required.</div>;
  }
  if (mapQuery.isLoading || !draft) return <div className="empty">Loading…</div>;

  return (
    <div style={{ padding: '1.5rem', maxWidth: 960, margin: '0 auto' }}>
      <p>
        <Link to="/admin/maps">← Admin maps</Link>
      </p>
      <h1>Edit map definition</h1>
      <p style={{ color: 'var(--muted)', marginTop: 0 }}>
        <code>{draft.mapId}</code>
      </p>
      <div className="form-grid">
        <label>
          Name
          <input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </label>
        <label>
          Description
          <textarea
            rows={3}
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
        </label>
        <label>
          Record label
          <input
            value={draft.recordLabel}
            onChange={(e) => setDraft({ ...draft, recordLabel: e.target.value })}
          />
        </label>
        <label>
          Filters (comma-separated field keys)
          <input
            value={draft.filters.join(', ')}
            onChange={(e) =>
              setDraft({
                ...draft,
                filters: e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
          />
        </label>
        <label>
          Table columns
          <input
            value={draft.tableColumns.join(', ')}
            onChange={(e) =>
              setDraft({
                ...draft,
                tableColumns: e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
          />
        </label>
        <label>
          Tooltip fields
          <span style={{ display: 'block', fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 400 }}>
            Field keys shown on map hover (comma-separated), e.g. name, role, publicSector
          </span>
          <input
            value={draft.tooltipFields.join(', ')}
            onChange={(e) =>
              setDraft({
                ...draft,
                tooltipFields: e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
          />
        </label>
        <label>
          Detail fields
          <input
            value={draft.detailFields.join(', ')}
            onChange={(e) =>
              setDraft({
                ...draft,
                detailFields: e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
          />
        </label>

        <h2>Fields</h2>
        <MapFieldsEditor
          fields={draft.fields}
          lockedKeys={lockedKeys}
          onChange={(fields) => setDraft({ ...draft, fields })}
        />

        <div className="form-actions">
          <button
            type="button"
            className="btn"
            disabled={updateMap.isPending}
            onClick={async () => {
              try {
                await updateMap.mutateAsync({ mapId, patch: draft });
                setToast({ message: 'Map definition saved.', type: 'success' });
              } catch (err) {
                setToast({
                  message: err instanceof Error ? err.message : 'Save failed',
                  type: 'error',
                });
              }
            }}
          >
            {updateMap.isPending ? 'Saving…' : 'Save definition'}
          </button>
        </div>
      </div>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
}
